import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, TextInput } from "@tremor/react";
import { useTranslation } from "react-i18next";
import logo from "../assets/logo-primary.svg";
import { registerWithPassword } from "../api/auth.js";
import { API_BASE_URL } from "../consts.js";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853" />
      <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
    </svg>
  );
}

export default function Signup() {
  const { t } = useTranslation("signup");
  const [searchParams] = useSearchParams();

  const invitationToken = searchParams.get("workspace_invitation_token");
  const isInvited = !!invitationToken;

  const [email, setEmail] = useState(() => searchParams.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [done, setDone] = useState(false);

  function handleGoogleSignup() {
    setError(null);
    setGoogleLoading(true);
    // The Google OAuth callback always returns to /login, which completes the
    // session — so "sign up with Google" and "log in with Google" share the
    // same entry point. Forward the invitation token when present.
    const base = API_BASE_URL + "/auth/google/start";
    const url = invitationToken
      ? `${base}?workspace_invitation_token=${encodeURIComponent(invitationToken)}`
      : base;
    window.location.assign(url);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError(t("errors.mismatch"));
      return;
    }
    if (password.length < 8) {
      setError(t("errors.too_short"));
      return;
    }

    setLoading(true);
    try {
      await registerWithPassword(email, password, fullName, null, invitationToken);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error ?? t("errors.failed"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-10">
      <div className="w-full max-w-lg rounded-xl bg-white p-8 shadow-sm ring-1 ring-gray-200">
        <div className="mb-6 flex flex-col items-center gap-3">
          <img src={logo} alt="Monitero" className="h-8 w-auto" />
          <div className="text-center">
            {isInvited ? (
              <h1 className="text-xl font-bold text-gray-900">{t("title_invited")}</h1>
            ) : (
              <>
                <h1 className="text-xl font-bold text-gray-900">{t("title_new")}</h1>
                <p className="mt-0.5 text-sm text-gray-500">{t("subtitle_new")}</p>
              </>
            )}
          </div>
        </div>

        {done ? (
          <div className="space-y-4 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-800">{t("done.title")}</p>
            <p className="text-sm text-gray-500">
              {t("done.description", { email })}
              {isInvited ? t("done.description_invited") : "."}</p>
            <Link to="/login" className="block text-sm font-medium text-teal-600 hover:text-teal-700">
              {t("done.back_to_login")}
            </Link>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={handleGoogleSignup}
              disabled={loading || googleLoading}
              className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
            >
              <GoogleIcon />
              {googleLoading ? t("form.google_connecting") : t("form.google")}
            </button>

            <div className="my-4 flex items-center gap-3">
              <hr className="flex-1 border-gray-200" />
              <span className="text-xs text-gray-400">{t("form.or")}</span>
              <hr className="flex-1 border-gray-200" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name + email */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("form.full_name")}</label>
                <TextInput
                  type="text"
                  autoComplete="name"
                  value={fullName}
                  onValueChange={setFullName}
                  placeholder="Jane Smith"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("form.email")}</label>
                <TextInput
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onValueChange={isInvited ? undefined : setEmail}
                  readOnly={isInvited}
                  placeholder="you@example.com"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("form.password")}</label>
                <TextInput
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onValueChange={setPassword}
                  placeholder={t("form.password_placeholder")}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("form.confirm_password")}</label>
                <TextInput
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onValueChange={setConfirmPassword}
                  placeholder="••••••••"
                  className="mt-1"
                />
              </div>
            </div>

            {/* New workspaces start on a free trial; the plan is chosen later
                in Billing settings once the user knows what they need. */}
            {!isInvited && (
              <p className="rounded-md bg-teal-50 px-3 py-2 text-xs text-teal-700">
                {t("form.trial_info")}
              </p>
            )}

            {error && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t("form.creating") : t("form.submit")}
            </Button>

            <p className="text-center text-sm text-gray-500">
              {t("form.already_have")}{" "}
              <Link to="/login" className="font-medium text-teal-600 hover:text-teal-700">
                {t("form.sign_in")}
              </Link>
            </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
